// File de ventes en attente — vague pilote 33.
//
// Quand le caissier valide une vente alors que le réseau est down ou que
// PostgREST renvoie une erreur réseau, on persiste la requête dans IndexedDB
// (survit au refresh / fermeture onglet). Un drainer en arrière-plan rejoue
// la file dès que la connexion revient.
//
// Stockage : IndexedDB natif sans dépendance. Une seule store "offline_sales"
// avec le payload complet `CreateSaleInput` + un client_id local pour
// l'idempotence et l'affichage UI.

import type { CreateSaleInput } from './sales'

const DB_NAME = 'pharmaconnect-offline'
const DB_VERSION = 1
const STORE = 'offline_sales'

export interface QueuedSale {
  id: string                     // UUID local
  payload: CreateSaleInput
  queued_at: string              // ISO
  attempts: number
  last_error: string | null
}

let cachedDb: IDBDatabase | null = null

const openDb = (): Promise<IDBDatabase> => {
  if (cachedDb) return Promise.resolve(cachedDb)
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'))
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => {
      cachedDb = req.result
      resolve(req.result)
    }
  })
}

const tx = async <T,>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | T,
): Promise<T> => {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, mode)
    const store = transaction.objectStore(STORE)
    let result: T | undefined
    const r = fn(store)
    if (r && typeof r === 'object' && 'onsuccess' in r) {
      const req = r as IDBRequest<T>
      req.onsuccess = () => {
        result = req.result
      }
      req.onerror = () => reject(req.error)
    } else {
      result = r as T
    }
    transaction.oncomplete = () => resolve(result as T)
    transaction.onerror = () => reject(transaction.error)
  })
}

const generateId = (): string =>
  `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

export const enqueueSale = async (payload: CreateSaleInput): Promise<QueuedSale> => {
  const entry: QueuedSale = {
    id: generateId(),
    payload,
    queued_at: new Date().toISOString(),
    attempts: 0,
    last_error: null,
  }
  await tx('readwrite', (store) => store.add(entry))
  return entry
}

export const listQueuedSales = async (): Promise<QueuedSale[]> => {
  return tx('readonly', (store) => store.getAll() as IDBRequest<QueuedSale[]>)
}

export const countQueuedSales = async (): Promise<number> => {
  return tx('readonly', (store) => store.count())
}

export const removeQueuedSale = async (id: string): Promise<void> => {
  await tx('readwrite', (store) => store.delete(id))
}

export const updateQueuedSale = async (
  id: string,
  patch: Partial<Pick<QueuedSale, 'attempts' | 'last_error'>>,
): Promise<void> => {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readwrite')
    const store = transaction.objectStore(STORE)
    const getReq = store.get(id)
    getReq.onsuccess = () => {
      const existing = getReq.result as QueuedSale | undefined
      if (!existing) {
        resolve()
        return
      }
      const updated = { ...existing, ...patch }
      store.put(updated)
    }
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}
